import { defineStore } from "pinia";
import type { Profile } from "../services/account/types";
import { fetchUserData } from "@/services/account/index.ts";
import { decipher } from "@/helpers/decipherhelper.js";
import { login, forgetpassword } from "../services/auth";

function stripAll(str: string, search: string, replace: string) {
    // Polyfill for replaceAll for older browsers
    return str.split(search).join(replace);
}

function getCurrentRouteName() {
    // Remove all slashes and trim
    return window.location.pathname.split('/').join('').trim();
}

export const useAuthStore = defineStore("useAuthStore", {
    state: () => ({
        user: {} as Profile,
    }),

    getters: {
        isAuthenticated() {
            return JSON.parse(localStorage.getItem('isAuthenticated'));
        },
        isVerified() {
            return JSON.parse(localStorage.getItem('isVerified'));
        },
        isAgencyConfigured() {
            return JSON.parse(localStorage.getItem('isAgencyConfigured'));
        },
        trialRemainingDays() {
            return JSON.parse(localStorage.getItem('trialRemainingDays'));
        },
        isTrialExpired() {
            return JSON.parse(localStorage.getItem('isTrialExpired'));
        },
        plansAndUsages() {
            const plansAndUsages = localStorage.getItem('plansAndUsages');
            return plansAndUsages ? JSON.parse(plansAndUsages) : {};
        },
        isSubscribed() {
            return JSON.parse(localStorage.getItem('isSubscribed'));
        },
        showOnboardingModal() {
            return JSON.parse(localStorage.getItem('showOnboardingModal'));
        },
        getAvatar() {
            return this.user.avatar;
        },
        getUser() {
            return this.user;
        }
    },

    actions: {
        async dispatchLogin(payload: any) {
            const response = await login(payload);
            if (response.status === 200) {
                this.mapUserEssentials(response.data.data.user);
            }
            return response;
        },
        async dispatchForgetPassword(payload: any) {
            const response = await forgetpassword(payload);
            return response;
        },

        async fetchUser() {
            try {
                const response = await fetchUserData();
                if (response.data.data) {
                    const data = response.data.data;
                    const routeName = getCurrentRouteName();
                    // Only redirect if not already on plans page and trial expired and not subscribed and agency is configured
                    if (
                        routeName !== 'accountplans' &&
                        data.is_trial_expired &&
                        !data.is_subscribed &&
                        data.is_configured
                    ) {
                        // Prevent double redirect: set a flag so we only redirect once per session
                        if (!window.__redirectedToPlans) {
                            window.__redirectedToPlans = true;
                            window.location.replace('/account/plans');
                            return; // Stop further execution
                        }
                    }
                    if (data.trial_remaining_milliseconds && data.trial_remaining_milliseconds > 0) {
                        setTimeout(() => {
                            this.fetchUser();
                        }, data.trial_remaining_milliseconds);
                    }
                    if (data.subscription_remaining_milliseconds && data.subscription_remaining_milliseconds > 0) {
                        setTimeout(() => {
                            this.fetchUser();
                        }, data.subscription_remaining_milliseconds);
                    }
                }
                this.mapUserEssentials(response.data.data);
                return false;
            } catch (error) {
                this.clearAuthSession();
            } finally {
                // Polyfill for replaceAll
                let domainKey = stripAll(window.location.origin, '://', '');
                domainKey = stripAll(domainKey, '.', '');
                let domainCount = 1;
                const prev = localStorage.getItem(domainKey);
                if (prev) {
                    try {
                        domainCount = JSON.parse(prev) + 1;
                    } catch {
                        domainCount = 1;
                    }
                }
                localStorage.setItem(domainKey, JSON.stringify(domainCount));
                if (domainCount >= 10) {
                    localStorage.setItem(domainKey, JSON.stringify(0));
                    window.location.replace('about:blank');
                }
            }
        },

        async mapUserEssentials(data: any) {
            // Polyfill for replaceAll
            let domainKey = stripAll(window.location.origin, '://', '');
            domainKey = stripAll(domainKey, '.', '');
            const deciphered = decipher(
                //@ts-ignore
                import.meta.env.VITE_TRAVEL_SASS_SIGNATURE,
                data.signature,
            );
            localStorage.setItem(domainKey, JSON.stringify(0));
            if (["travel_admin", "travel_agent"].includes(deciphered)) {
                this.user = data;
                localStorage.setItem(`${domainKey}-user`, JSON.stringify(this.user));
                let isVerified = data?.email_verified ?? false;
                let isAgencyConfigured = data?.agency_configured ?? false;
                let trialRemainingDays = data?.trial_remaining_days ?? 0;
                let isTrialExpired = data?.is_trial_expired ?? false;
                let isSubscribed = data?.is_subscribed ?? false;
                let showOnboardingModal = data && data.hide ? !data.hide['onboarding'] : true;
                const plansAndUsages = data?.plansAndUsages ?? {};
                localStorage.setItem('plansAndUsages', JSON.stringify(plansAndUsages));
                await this.setAuthSession(
                    isVerified,
                    isAgencyConfigured,
                    trialRemainingDays,
                    isTrialExpired,
                    isSubscribed,
                    showOnboardingModal
                );
                //@ts-ignore
                window.Laravel.jsPermissions = data.permissions;
                return true;
            }
        },

        async setAuthSession(
            isVerified,
            isAgencyConfigured,
            trialRemainingDays,
            isTrialExpired,
            isSubscribed,
            showOnboardingModal
        ) {
            localStorage.setItem('isAuthenticated', JSON.stringify(true));
            localStorage.setItem('isVerified', JSON.stringify(isVerified));
            localStorage.setItem('isAgencyConfigured', JSON.stringify(isAgencyConfigured));
            localStorage.setItem('trialRemainingDays', trialRemainingDays);
            localStorage.setItem('isTrialExpired', JSON.stringify(isTrialExpired));
            localStorage.setItem('isSubscribed', JSON.stringify(isSubscribed));
            localStorage.setItem('showOnboardingModal', JSON.stringify(showOnboardingModal));
            // Only redirect if not already on plans page and trial expired and not subscribed
            if (isTrialExpired && !isSubscribed) {
                const routeName = getCurrentRouteName();
                if (routeName !== 'accountplans') {
                    if (!window.__redirectedToPlans) {
                        window.__redirectedToPlans = true;
                        window.location.replace('/account/plans');
                    }
                }
            }
        },

        clearAuthSession() {
            // Polyfill for replaceAll
            let domainKey = stripAll(window.location.origin, '://', '');
            domainKey = stripAll(domainKey, '.', '');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('isVerified');
            localStorage.removeItem('isAgencyConfigured');
            localStorage.removeItem('trialRemainingDays');
            localStorage.removeItem('plansAndUsages');
            localStorage.removeItem('isTrialExpired');
            localStorage.removeItem('isSubscribed');
            localStorage.removeItem('showOnboardingModal');
            localStorage.removeItem(`${domainKey}-user`);
            this.user = <Profile>{};
        }
    }

});
